<script setup lang="ts">
interface SceneViewOption {
  value: string
  label: string
}

interface ControlModeOption {
  value: string
  label: string
  detail?: string
}

interface SecondaryControlGroup {
  id: string
  title: string
  subtitle?: string
  options: ControlModeOption[]
  activeValue?: string | null
}

withDefaults(
  defineProps<{
    title?: string
    subtitle?: string
    badge: string
    isPerspective: boolean
    options: SceneViewOption[]
    controlModeOptions?: ControlModeOption[]
    activeControlMode?: string | null
    controlTitle?: string
    controlSubtitle?: string
    secondaryControlOptions?: ControlModeOption[]
    activeSecondaryControlMode?: string | null
    secondaryControlTitle?: string
    secondaryControlSubtitle?: string
    secondaryControlGroups?: SecondaryControlGroup[]
    modeToggleDisabled?: boolean
    modeToggleLabel?: string
    fitLabel?: string
  }>(),
  {
    title: '视图类',
    subtitle: '相机切换、预设视角与整体适配',
    controlModeOptions: () => [],
    activeControlMode: null,
    controlTitle: '控制器',
    controlSubtitle: '切换不同的相机控制器',
    secondaryControlOptions: () => [],
    activeSecondaryControlMode: null,
    secondaryControlTitle: '交互细节',
    secondaryControlSubtitle: '针对当前控制器进一步调整交互方式',
    secondaryControlGroups: () => [],
    modeToggleDisabled: false,
    modeToggleLabel: '',
    fitLabel: '适配全部',
  },
)

const emit = defineEmits<{
  (event: 'toggle-mode'): void
  (event: 'fit'): void
  (event: 'set-view', value: string): void
  (event: 'set-control-mode', value: string): void
  (event: 'set-secondary-control-mode', value: string): void
  (event: 'set-secondary-control-group-mode', payload: { groupId: string; value: string }): void
}>()

function handleButtonClick(
  event: MouseEvent,
  callback: () => void,
) {
  const target = event.currentTarget
  if (target instanceof HTMLButtonElement) {
    target.blur()
  }

  callback()
}

function handleButtonPointerDown(event: PointerEvent | MouseEvent) {
  event.preventDefault()
}
</script>

<template>
  <details class="panel-section" open>
    <summary class="panel-section-summary">
      <span class="summary-main">
        <span class="panel-section-title">{{ title }}</span>
        <span class="panel-section-subtitle">{{ subtitle }}</span>
      </span>
      <span class="summary-side">
        <span class="summary-count">{{ badge }}</span>
        <span class="summary-chevron" aria-hidden="true">▾</span>
      </span>
    </summary>

    <div class="panel-section-body">
      <div class="view-actions">
        <button
          class="panel-button"
          :disabled="modeToggleDisabled"
          @pointerdown="handleButtonPointerDown"
          @click="handleButtonClick($event, () => emit('toggle-mode'))"
        >
          {{ modeToggleLabel || (isPerspective ? '切换正交' : '切换透视') }}
        </button>
        <button
          class="panel-button panel-button-subtle"
          @pointerdown="handleButtonPointerDown"
          @click="handleButtonClick($event, () => emit('fit'))"
        >
          {{ fitLabel }}
        </button>
      </div>

      <div v-if="controlModeOptions.length > 0" class="view-control-group">
        <div class="view-control-header">
          <span class="view-control-title">{{ controlTitle }}</span>
          <span class="view-control-subtitle">{{ controlSubtitle }}</span>
        </div>

        <div class="button-grid view-control-grid">
          <button
            v-for="item in controlModeOptions"
            :key="item.value"
            class="panel-button"
            :class="{ active: activeControlMode === item.value }"
            @pointerdown="handleButtonPointerDown"
            @click="handleButtonClick($event, () => emit('set-control-mode', item.value))"
          >
            <span class="panel-button-label">{{ item.label }}</span>
            <span class="panel-button-meta">{{ item.detail || '切换当前控制器' }}</span>
          </button>
        </div>
      </div>

      <template v-if="secondaryControlGroups.length > 0">
        <div v-for="group in secondaryControlGroups" :key="group.id" class="view-control-group">
          <div class="view-control-header">
            <span class="view-control-title">{{ group.title }}</span>
            <span class="view-control-subtitle">{{ group.subtitle || '针对当前控制器进一步调整交互方式' }}</span>
          </div>

          <div class="button-grid view-control-grid">
            <button
              v-for="item in group.options"
              :key="`${group.id}-${item.value}`"
              class="panel-button"
              :class="{ active: group.activeValue === item.value }"
              @pointerdown="handleButtonPointerDown"
              @click="
                handleButtonClick($event, () =>
                  emit('set-secondary-control-group-mode', {
                    groupId: group.id,
                    value: item.value,
                  }))
              "
            >
              <span class="panel-button-label">{{ item.label }}</span>
              <span class="panel-button-meta">{{ item.detail || '切换当前交互方式' }}</span>
            </button>
          </div>
        </div>
      </template>

      <div v-else-if="secondaryControlOptions.length > 0" class="view-control-group">
        <div class="view-control-header">
          <span class="view-control-title">{{ secondaryControlTitle }}</span>
          <span class="view-control-subtitle">{{ secondaryControlSubtitle }}</span>
        </div>

        <div class="button-grid view-control-grid">
          <button
            v-for="item in secondaryControlOptions"
            :key="item.value"
            class="panel-button"
            :class="{ active: activeSecondaryControlMode === item.value }"
            @pointerdown="handleButtonPointerDown"
            @click="handleButtonClick($event, () => emit('set-secondary-control-mode', item.value))"
          >
            <span class="panel-button-label">{{ item.label }}</span>
            <span class="panel-button-meta">{{ item.detail || '切换当前交互方式' }}</span>
          </button>
        </div>
      </div>

      <div class="button-grid button-grid-three view-preset-grid">
        <button
          v-for="item in options"
          :key="item.value"
          class="panel-button"
          @pointerdown="handleButtonPointerDown"
          @click="handleButtonClick($event, () => emit('set-view', item.value))"
        >
          <span class="panel-button-label">{{ item.label }}</span>
          <span class="panel-button-meta">快速切换预设视角</span>
        </button>
      </div>
    </div>
  </details>
</template>

<style scoped>
.view-control-group {
  margin-top: 12px;
}

.view-control-header {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-bottom: 10px;
}

.view-control-title {
  color: #f4f7fb;
  font-size: 13px;
  font-weight: 600;
}

.view-control-subtitle {
  color: rgba(214, 224, 235, 0.64);
  font-size: 11px;
}

.view-control-grid {
  gap: 10px;
}

.view-preset-grid {
  margin-top: 12px;
}
</style>
